import { useEffect, useState } from 'react';

/**
 * Theme control (brand §2 — the dark palette ships in index.css under :root[data-theme='dark'];
 * there is NO prefers-color-scheme auto-switch there, so the theme is driven ENTIRELY by the
 * data-theme attribute on <html>, which this module owns).
 *
 * Three choices — light / dark / system (default). The CHOICE is persisted; "system" follows the
 * OS and updates live when the OS flips. A tiny inline script in index.html applies the resolved
 * theme before first paint (no flash); this module keeps it in sync after React mounts.
 */
export type ThemeChoice = 'light' | 'dark' | 'system';

const KEY = 'alchmy.theme';
const mql = () => window.matchMedia('(prefers-color-scheme: dark)');

export function getStoredChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* private window / blocked storage — fall through to default */
  }
  return 'system';
}

/** The concrete theme a choice resolves to right now. */
export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice === 'system') {
    try {
      return mql().matches ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }
  return choice;
}

/** Set data-theme on <html> to the resolved theme. */
export function applyTheme(choice: ThemeChoice): void {
  document.documentElement.setAttribute('data-theme', resolveTheme(choice));
}

/**
 * React hook: owns the current choice, applies it, persists it, and — while on "system" — listens
 * for OS theme changes and re-applies live. Returns the choice + a setter.
 */
export function useTheme(): { choice: ThemeChoice; setChoice: (c: ThemeChoice) => void } {
  const [choice, setChoiceState] = useState<ThemeChoice>(getStoredChoice);

  // Apply + persist whenever the choice changes.
  useEffect(() => {
    applyTheme(choice);
    try {
      localStorage.setItem(KEY, choice);
    } catch {
      /* ignore — a per-viewer convenience */
    }
  }, [choice]);

  // On "system", re-apply when the OS theme flips at runtime (the piece index.css deferred).
  useEffect(() => {
    if (choice !== 'system') return;
    const m = mql();
    const onChange = () => applyTheme('system');
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, [choice]);

  return { choice, setChoice: setChoiceState };
}
